import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { RiCloseFill } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { resetLeadFilter, setLeadFilter } from '../../actions/actions';
import { FilterParams } from './leadsGrid/leads.types';
import { Ban, Copy, PhoneOff, UserMinus, Calendar, Clock, LucideProps, List, Bookmark, ChevronDown, UserPlus, CheckSquare, AlertCircle, Phone } from 'lucide-react';
import { filterLeads } from '../../services/leadService';
import DuplicateManager from './leadsGrid/DuplicateManager';
import QuickFilters from './QuickFilters';
import PopupComponent from '../../components/PopupComponent';
import logger, { Logger } from '../../utils/logger';



interface DuplicateFilterOptions {
  isDuplicate: boolean;
  list?: string;  // Optional as it may not always be present
  paginationParams: {
    page: number;
    perPage: number;
  };
}

interface IProps {
  // Change the prop name in the interface
  onFilterChange?: (filterData: Partial<FilterParams>) => void;
}


// Update FilterKey to include isDuplicate
type FilterKey = keyof Pick<FilterParams, 'isUntouched' | 'isNotCalled' | 'isNotAssign' | 'isFollowUp' | 'isStaled' | 'isDuplicate'>;

type IconType = React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>>;

const LeadsNewFilter: React.FC<IProps> = ({ onFilterChange }) => {
  const dispatch = useDispatch();
  const { filterParam } = useSelector((state: any) => state.rootReducers.leads);
  
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [leadListData, setLeadListData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
 
  const triggerRef = useRef<HTMLDivElement>(null);
  const [menuRect, setMenuRect] = useState<DOMRect | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allDuplicateLeads, setAllDuplicateLeads] = useState<any[]>([]);
  const [showSavedFiltersDropdown, setShowSavedFiltersDropdown] = useState(false);
  const [savedFiltersPosition, setSavedFiltersPosition] = useState<{ top: number; left: number } | null>(null);
  const savedFiltersRef = useRef<HTMLDivElement>(null);
  const [openDropdowns, setOpenDropdowns] = useState<{ [key: string]: boolean }>({});

  // Quick filter options for the "Saved Filters" dropdown
  type QuickFilterValue = 'recent_assign_leads' | 'today_task' | 'over_due_task' | 'recent_call';

  const savedFilterOptions: Array<{
    value: QuickFilterValue;
    label: string;
    icon: IconType;
    description: string;
  }> = [
    {
      value: 'recent_assign_leads',
      label: 'Recently Assigned',
      icon: UserPlus,
      description: 'Leads assigned to you recently'
    },
    {
      value: 'today_task',
      label: "Today's Tasks",
      icon: CheckSquare,
      description: 'Leads with tasks due today'
    },
    {
      value: 'over_due_task',
      label: 'Overdue Tasks',
      icon: AlertCircle,
      description: 'Leads with overdue tasks'
    },
    {
      value: 'recent_call',
      label: 'Recent Calls',
      icon: Phone,
      description: 'Leads with recent call activity'
    }
  ];
  
  const listId = useSelector((state: any) =>
    state.rootReducers.leadName.leadName.id
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdownElements = document.querySelectorAll('.dropdown-container');
      let clickedInsideDropdown = false;

      dropdownElements.forEach(element => {
        if (element.contains(event.target as Node)) {
          clickedInsideDropdown = true;
        }
      });

      if (!clickedInsideDropdown) {
        setOpenDropdowns({});
        setShowSavedFiltersDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdowns when filters change
  useEffect(() => {
    setOpenDropdowns({});
  }, [filterParam]);

  
  const tooltips = {
    isUntouched: "Leads you haven't reached out to yet. These are brand new leads waiting for initial contact.",
    isNotCalled: "Leads with no calls made, or calls where you couldn't connect. Use this to find leads that need calling.",
    isNotAssign: "Leads not yet assigned to any team member. These are unallocated leads waiting for assignment.",
    isFollowUp: "Leads with no follow-up tasks scheduled. Use this to find leads that need a follow-up activity.",
    isStaled: "Leads with no activity in the past 15 days. These leads may need re-engagement or nurturing.",
    isDuplicate: "Leads that have duplicate entries in the system. Click to open the duplicate manager and resolve conflicts.",
    savedFilters: "Quick access to common lead views. Selecting a saved filter will reset all other filters."
  };

  // Handler for selecting a saved/quick filter
  const handleSavedFilterSelect = (filterValue: QuickFilterValue) => {
    logger.log('Selecting saved filter:', filterValue);

    // Reset all filters first, then apply only the quickFilter
    dispatch(resetLeadFilter());

    // Apply the selected quick filter with pagination reset
    if (onFilterChange) {
      onFilterChange({
        quickFilter: filterValue,
        paginationParams: { page: 1, perPage: 50 }
      });
    }

    // Close the dropdown
    setShowSavedFiltersDropdown(false);
  };

  // Get the currently active saved filter label
  const getActiveSavedFilterLabel = (): string | null => {
    if (!filterParam.quickFilter) return null;
    const activeFilter = savedFilterOptions.find(opt => opt.value === filterParam.quickFilter);
    return activeFilter?.label || null;
  };

  // Clear the active saved filter
  const clearSavedFilter = (e: React.MouseEvent) => {
    e.stopPropagation();
    logger.log('Clearing saved filter');

    if (onFilterChange) {
      onFilterChange({
        quickFilter: undefined,
        paginationParams: { page: 1, perPage: 50 }
      });
    }
  };
  
  const handleResetFilters = useCallback(() => {
  
    dispatch(resetLeadFilter()); // Use the standard reset action
   
  }, [dispatch]);
  
  const filters: Array<{ key: FilterKey; label: string; icon: IconType }> = [
    { key: 'isUntouched', label: 'Untouched', icon: Ban },
    { key: 'isNotCalled', label: 'No call', icon: PhoneOff },
    { key: 'isNotAssign', label: 'Unassigned', icon: UserMinus },
    { key: 'isFollowUp', label: 'No task', icon: Calendar },
    { key: 'isStaled', label: 'Stale', icon: Clock },
    { key: 'isDuplicate', label: 'Duplicates', icon: Copy }
    
  ];

    
// In LeadsNewFilter.tsx
const handleFilterClick = async (key: FilterKey) => {

  logger.log('Current filter state:', filterParam);
  
  if (triggerRef.current) {
    setMenuRect(triggerRef.current.getBoundingClientRect());
  }
  
  
  
  // Special handling for duplicates filter
  if (key === 'isDuplicate') {
    logger.log('Handling duplicate filter');
    
    try {
      setShowDuplicateModal(true); // Show modal first
      setIsLoading(true); // Show loading state
      setCurrentPage(1);
      setAllDuplicateLeads([]);

      // Call API with just duplicate flag and list for modal data
      logger.log('Fetching duplicates for modal display');
      const response = await filterLeads({
        isDuplicate: true,
        list: listId === "0" ? undefined : listId,
        paginationParams: {
          perPage: 50,
          page: 1
        }
      });
      
      if (response?.status) {
        logger.log('Duplicates fetched successfully:', response.data.data.length);
        setAllDuplicateLeads(response.data.data);
        setHasMore(response.data.data.length === 50);
      }
    } catch (error) {
      console.error('[Filter] Error fetching duplicates:', error);
      setShowDuplicateModal(false); 
    } finally {
      setIsLoading(false);
    }
    
    // Also update filter state for grid data
    logger.log('Updating filter state for isDuplicate');
    if (onFilterChange) {
      onFilterChange({ 
        isDuplicate: true,
        paginationParams: { page: 1, perPage: 50 }
      });
    }

    return;
  }

  // For other filters, just call onFilterChange
  const newValue = !filterParam[key];
  logger.log(`Setting ${key} to ${newValue}`);
  
  if (onFilterChange) {
    onFilterChange({ 
      [key]: newValue || undefined,
      paginationParams: { page: 1, perPage: 50 }
    });
  }
  

};

const loadMoreDuplicates = async () => {
  if (!hasMore || isLoading) {
    Logger.log('[DuplicateManager] Skipping load more:', { hasMore, isLoading });
    return;
  }
  
  try {
    setIsLoading(true);
    const nextPage = currentPage + 1;
    
    const filterOptions = { 
      isDuplicate: true,
      list: filterParam.list,
      paginationParams: { 
        page: nextPage, 
        perPage: 50 
      }
    };

    Logger.log('[DuplicateManager] Loading more:', filterOptions);
    const response = await filterLeads(filterOptions);
    
    if (response?.status) {
      const newLeads = response.data.data;
      Logger.log('[DuplicateManager] Got new leads:', {
        newLeadsCount: newLeads.length,
        currentTotal: allDuplicateLeads.length,
        nextPage
      });
      
      // Check if we got any new leads
      if (newLeads.length === 0) {
        Logger.log('[DuplicateManager] No more leads to load');
        setHasMore(false);
        return;
      }

      setAllDuplicateLeads(prev => [...prev, ...newLeads]);
      setCurrentPage(nextPage);
      setHasMore(newLeads.length === 50);

      Logger.log('[DuplicateManager] Updated state:', {
        totalLeads: allDuplicateLeads.length + newLeads.length,
        hasMore: newLeads.length === 50,
        currentPage: nextPage
      });
    }
  } catch (error) {
    console.error('[DuplicateManager] Error loading more duplicates:', error);
    setHasMore(false); // Set hasMore to false on error
  } finally {
    setIsLoading(false);
  }
};

   return (
    <Root>
      <FilterContainer data-tour="filter-bar">
        <FilterList role="list">
          {/* Saved Filters Dropdown - First item on the left */}
          <SavedFiltersContainer ref={savedFiltersRef} className="dropdown-container" data-tour="saved-filters">
            <SavedFiltersTrigger
              onClick={() => {
                if (!showSavedFiltersDropdown && savedFiltersRef.current) {
                  const rect = savedFiltersRef.current.getBoundingClientRect();
                  setSavedFiltersPosition({
                    top: rect.bottom + 4,
                    left: rect.left
                  });
                }
                setShowSavedFiltersDropdown(prev => !prev);
              }}
              isActive={!!filterParam.quickFilter}
              title={tooltips.savedFilters}
            >
              <SavedFiltersContent>
                <Bookmark size={14} />
                <span>{getActiveSavedFilterLabel() || 'Saved Filters'}</span>
              </SavedFiltersContent>
              {filterParam.quickFilter ? (
                <CloseButton onClick={clearSavedFilter} aria-label="Clear saved filter">
                  <RiCloseFill size={14} />
                </CloseButton>
              ) : (
                <ChevronDown size={14} style={{ marginLeft: '4px', opacity: 0.7 }} />
              )}
            </SavedFiltersTrigger>

            {showSavedFiltersDropdown && savedFiltersPosition && (
              <SavedFiltersDropdown $top={savedFiltersPosition.top} $left={savedFiltersPosition.left}>
                <DropdownHeader>
                  <span>Quick Access Filters</span>
                  <small>Select to reset all filters and apply</small>
                </DropdownHeader>
                {savedFilterOptions.map(({ value, label: optLabel, icon: OptionIcon, description }) => (
                  <SavedFilterOption
                    key={value}
                    onClick={() => handleSavedFilterSelect(value)}
                    isActive={filterParam.quickFilter === value}
                  >
                    <OptionIcon size={16} />
                    <OptionDetails>
                      <OptionLabel>{optLabel}</OptionLabel>
                      <OptionDescription>{description}</OptionDescription>
                    </OptionDetails>
                    {filterParam.quickFilter === value && (
                      <ActiveIndicator>✓</ActiveIndicator>
                    )}
                  </SavedFilterOption>
                ))}
              </SavedFiltersDropdown>
            )}
          </SavedFiltersContainer>

          <Separator aria-hidden="true">•</Separator>

          {/* Filter buttons */}
          {filters.map(({ key, label, icon: Icon }, index) => (
            <React.Fragment key={key}>
              {index > 0 && <Separator aria-hidden="true">•</Separator>}
              <FilterItem
                isActive={!!filterParam[key]}
                onClick={() => handleFilterClick(key)}
                aria-label={`Filter by ${label}${filterParam[key] ? ' (active)' : ''}`}
                title={tooltips[key]}
                role="listitem"
              >
                <FilterContent>
                  <Icon size={14} className="filter-icon" aria-hidden="true" />
                  <FilterLabel>{label}</FilterLabel>
                  {filterParam[key] && (
                    <CloseButton
                      aria-label={`Remove ${label} filter`}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleFilterClick(key);
                      }}
                    >
                      <RiCloseFill size={14} />
                    </CloseButton>
                  )}
                </FilterContent>
              </FilterItem>
            </React.Fragment>
          ))}
          <QuickFilters onFilterChange={onFilterChange} />
        </FilterList>
        
        <PopupComponent
          isOpen={showDuplicateModal}
          onClose={() => {
            setShowDuplicateModal(false);
            setCurrentPage(1);
            setAllDuplicateLeads([]);
          }}
          title="Duplicate Leads Manager"
          size="lg"
          loading={isLoading} 
        >
          <DuplicateManager 
            leads={allDuplicateLeads}
            onClose={() => setShowDuplicateModal(false)}
            onLoadMore={loadMoreDuplicates}
            hasMore={hasMore}
            isLoading={isLoading}
          />
        </PopupComponent>
      </FilterContainer>
    </Root>
  );
};



const Root = styled.section`
  padding: 0.5rem 1rem;
  background: #ffffff;
  border-bottom: 1px solid #f1f5f9;
  width: 100%;
  position: relative;
  overflow: visible;
  border-radius: 0 0 0.5rem 0.5rem;
  margin: 0;
  box-shadow: 0 0.0625rem 0.125rem rgba(0, 0, 0, 0.05);
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2rem;
  z-index: 1;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const FilterHeader = styled.div`
  color: rgb(17, 44, 82);
  font-size: 0.75rem;
  white-space: nowrap;
  user-select: none;
  padding-left: 0.125rem;
`;

const FilterList = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 2px;
  position: relative;
  z-index: 50;
  flex-wrap: nowrap;
  flex-shrink: 0;
`;

const Separator = styled.span`
  color: rgb(0, 0, 0);
  margin: 0 3px;
  font-size: 5px;
  line-height: 1;
  user-select: none;
  vertical-align: middle;
  font-weight: bold;
`;

const FilterContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  height: 24px;

  .filter-icon {
    color: currentColor;
    opacity: 0.8;
    flex-shrink: 0;
    width: 12px;
    height: 12px;
  }
`;

const FilterLabel = styled.span`
  white-space: nowrap;
  line-height: 1;
  font-size: 12px;
`;

const FilterItem = styled.li<{ isActive?: boolean }>`
  position: relative;
  color: ${props => props.isActive ? '#1e40af' : '#64748b'};
  background: ${props => props.isActive ? '#dbeafe' : '#f8fafc'};
  border: 1px solid ${props => props.isActive ? '#93c5fd' : '#e2e8f0'};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  user-select: none;
  height: 24px;
  display: flex;
  align-items: center;

  &:hover {
    background: ${props => props.isActive ? '#bfdbfe' : '#f1f5f9'};
    border-color: ${props => props.isActive ? '#60a5fa' : '#cbd5e1'};
    box-shadow: 0 0.0625rem 0.125rem rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(0.0625rem);
    box-shadow: none;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem;
  margin-left: 0.125rem;
  color: inherit;
  background: none;
  border: none;
  border-radius: 0.1875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0.8;

  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.06);
  }

  &:active {
    transform: scale(0.95);
  }
`;
const ResetButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
`;

// Saved Filters Dropdown Styles
const SavedFiltersContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  z-index: 100;
`;

const SavedFiltersTrigger = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  height: 24px;
  background: ${props => props.isActive ? '#dbeafe' : '#f8fafc'};
  border: 1px solid ${props => props.isActive ? '#93c5fd' : '#e2e8f0'};
  border-radius: 4px;
  color: ${props => props.isActive ? '#1e40af' : '#64748b'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${props => props.isActive ? '#bfdbfe' : '#f1f5f9'};
    border-color: ${props => props.isActive ? '#60a5fa' : '#cbd5e1'};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const SavedFiltersContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SavedFiltersDropdown = styled.div<{ $top?: number; $left?: number }>`
  position: fixed;
  top: ${props => props.$top ? `${props.$top}px` : 'auto'};
  left: ${props => props.$left ? `${props.$left}px` : 'auto'};
  min-width: 280px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 5px 10px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  padding: 12px 16px;
  background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
  border-bottom: 2px solid #93c5fd;

  span {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #1e40af;
  }

  small {
    display: block;
    font-size: 12px;
    color: #3b82f6;
    margin-top: 2px;
  }
`;

const SavedFilterOption = styled.div<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.isActive ? '#eff6ff' : 'white'};
  border-left: 3px solid ${props => props.isActive ? '#3b82f6' : 'transparent'};

  &:hover {
    background: #f1f5f9;
    transform: translateX(2px);
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }

  svg {
    color: ${props => props.isActive ? '#3b82f6' : '#64748b'};
    flex-shrink: 0;
  }
`;

const OptionDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const OptionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
`;

const OptionDescription = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`;

const ActiveIndicator = styled.span`
  color: #3b82f6;
  font-weight: 600;
  font-size: 14px;
`;

export default LeadsNewFilter;